import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core'

import { SelectionManager } from '../../../common/utilities/selection-manager'
import { ModalService } from '../../../providers/modal/modal.service'
import { AssetPreviewDialogComponent } from '../asset-preview-dialog/asset-preview-dialog.component'

import { AssetLike } from './asset-gallery.types'

@Component({
  selector: 'vdr-asset-gallery',
  templateUrl: './asset-gallery.component.html',
  styleUrls: ['./asset-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetGalleryComponent implements OnChanges {
  @Input() assets: AssetLike[]
  /**
   * If true, allows multiple assets to be selected by ctrl+clicking.
   */
  @Input() multiSelect = false
  @Input() canDelete = false
  @Output() selectionChange = new EventEmitter<AssetLike[]>()
  @Output() deleteAssets = new EventEmitter<AssetLike[]>()
  @Output() editAssetClick = new EventEmitter<void>()

  selectionManager = new SelectionManager<AssetLike>({
    multiSelect: this.multiSelect,
    itemsAreEqual: (a, b) => a.id === b.id,
    additiveMode: false
  })

  constructor(
    private modalService: ModalService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (this.assets) {
      for (const asset of this.selectionManager.selection) {
        // Update any selected assets with any changes
        const match = this.assets.find((a) => a.id === asset.id)
        if (match) {
          Object.assign(asset, match)
        }
      }
    }
    if (changes['assets']) {
      this.selectionManager.setCurrentItems(this.assets)
    }
    if (changes['multiSelect']) {
      this.selectionManager.setMultiSelect(this.multiSelect)
    }
  }

  focusAsset: AssetLike | undefined
  isPreviewLoading = false

  toggleSelection(asset: AssetLike, event?: MouseEvent) {
    // Ngăn bubble
    if (event) {
      event.stopPropagation()
      event.preventDefault()
    }

    // Pass through the real event so shift+click range selection works
    const fakeEvent = {
      ctrlKey: true,
      metaKey: true,
      shiftKey: event?.shiftKey ?? false
    } as MouseEvent

    this.selectionManager.toggleSelection(asset, fakeEvent)
    this.focusAsset = undefined // Clear focus to show last selected
    this.isPreviewLoading = true
    // Emit with new array reference to ensure change detection
    this.selectionChange.emit([...this.selectionManager.selection])
    this.changeDetector.markForCheck()
  }

  deselectAsset(asset: AssetLike, event: MouseEvent) {
    event.stopPropagation()
    event.preventDefault()
    const index = this.selectionManager.selection.findIndex(
      (a) => a.id === asset.id
    )
    if (index !== -1) {
      this.selectionManager.selection.splice(index, 1)
      // Emit with new array reference to ensure change detection
      this.selectionChange.emit([...this.selectionManager.selection])
      this.changeDetector.markForCheck()
    }
  }

  selectMultiple(assets: AssetLike[]) {
    this.selectionManager.selectMultiple(assets)
    this.focusAsset = undefined // Clear focus
    this.isPreviewLoading = true
    // Emit with new array reference to ensure change detection
    this.selectionChange.emit([...this.selectionManager.selection])
    this.changeDetector.markForCheck()
  }

  isSelected(asset: AssetLike): boolean {
    return this.selectionManager.isSelected(asset)
  }

  get activeAsset(): AssetLike {
    if (this.focusAsset && this.isSelected(this.focusAsset)) {
      return this.focusAsset
    }
    return this.selectionManager.lastSelected()
  }

  lastSelected(): AssetLike {
    return this.selectionManager.lastSelected()
  }

  previewAsset(asset: AssetLike) {
    this.modalService
      .fromComponent(AssetPreviewDialogComponent, {
        size: 'xl',
        closable: true,
        locals: { asset, assets: this.assets }
      })
      .subscribe()
  }

  entityInfoClick(event: MouseEvent) {
    event.preventDefault()
    event.stopPropagation()
  }

  selectAssetInList(asset: AssetLike) {
    if (this.focusAsset?.id !== asset.id) {
      this.focusAsset = asset
      this.isPreviewLoading = true
    }
  }

  onPreviewImageLoad() {
    this.isPreviewLoading = false
  }
}
